/* global document, HTMLElement, KeyboardEvent */

import type { AddinOrganisation } from '../../organisations/addin-organisation.model';

export interface OrganisationSelectorDialogOptions {
    organisations: readonly AddinOrganisation[];
    selectedCode: string | null;
    title: string;
    confirmLabel: string;
    /** Empty string hides Cancel and keeps the dialog open until the user confirms (Trace login). */
    cancelLabel: string;
}

const DIALOG_ID = 'organisation-selector-dialog';
const TITLE_ID = 'organisation-selector-title';
const LIST_ID = 'organisation-selector-list';
const CANCEL_ID = 'organisation-selector-cancel';
const CONFIRM_ID = 'organisation-selector-confirm';
const BACKDROP_ID = 'organisation-selector-backdrop';

/**
 * In-pane organisation list. Same choices as Trace's organisation selector:
 * login requires a choice; switch can be cancelled and does not sign the user out.
 */
export function openOrganisationSelectorDialog(
    options: OrganisationSelectorDialogOptions
): Promise<AddinOrganisation | undefined> {
    const root = document.getElementById(DIALOG_ID);
    const title = document.getElementById(TITLE_ID);
    const list = document.getElementById(LIST_ID);
    const cancelBtn = document.getElementById(CANCEL_ID) as HTMLButtonElement | null;
    const confirmBtn = document.getElementById(CONFIRM_ID) as HTMLButtonElement | null;
    const backdrop = document.getElementById(BACKDROP_ID);

    if (!root || !title || !list || !cancelBtn || !confirmBtn) {
        return Promise.resolve(undefined);
    }

    const allowCancel = options.cancelLabel.trim().length > 0;
    const preselected = options.selectedCode?.trim() ?? '';

    title.textContent = options.title;
    confirmBtn.textContent = options.confirmLabel;
    cancelBtn.textContent = allowCancel ? options.cancelLabel : 'Cancel';
    cancelBtn.classList.remove('hidden');
    cancelBtn.hidden = !allowCancel;
    renderOptions(list, options.organisations, preselected);
    syncConfirmEnabled(list, confirmBtn);

    return new Promise((resolve) => {
        const previousFocus =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        let settled = false;

        const finish = (organisation: AddinOrganisation | undefined) => {
            if (settled) {
                return;
            }
            settled = true;
            root.classList.add('hidden');
            list.removeEventListener('change', onListChange);
            cancelBtn.removeEventListener('click', onCancel);
            confirmBtn.removeEventListener('click', onConfirm);
            backdrop?.removeEventListener('click', onBackdrop);
            document.removeEventListener('keydown', onKeyDown);
            previousFocus?.focus();
            resolve(organisation);
        };

        const onListChange = () => syncConfirmEnabled(list, confirmBtn);
        const onCancel = () => finish(undefined);
        const onBackdrop = () => {
            if (allowCancel) {
                finish(undefined);
            }
        };
        const onConfirm = () => {
            const code = selectedRadioValue(list);
            const organisation = options.organisations.find(
                (item) => item.organisationCode === code
            );
            if (!organisation) {
                syncConfirmEnabled(list, confirmBtn);
                return;
            }
            finish(organisation);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && allowCancel) {
                event.preventDefault();
                finish(undefined);
            }
        };

        list.addEventListener('change', onListChange);
        cancelBtn.addEventListener('click', onCancel);
        confirmBtn.addEventListener('click', onConfirm);
        backdrop?.addEventListener('click', onBackdrop);
        document.addEventListener('keydown', onKeyDown);

        root.classList.remove('hidden');
        const checked = list.querySelector<HTMLInputElement>('input[type="radio"]:checked');
        const firstRadio = list.querySelector<HTMLInputElement>('input[type="radio"]');
        (checked ?? firstRadio ?? confirmBtn).focus();
    });
}

function renderOptions(
    list: HTMLElement,
    organisations: readonly AddinOrganisation[],
    selectedCode: string
): void {
    list.replaceChildren();
    for (const organisation of organisations) {
        const option = document.createElement('label');
        option.className = 'org-selector__option';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'organisation-selector-option';
        input.className = 'org-selector__radio';
        input.value = organisation.organisationCode;
        input.checked = organisation.organisationCode === selectedCode;

        const text = document.createElement('span');
        text.className = 'org-selector__label';

        const name = document.createElement('span');
        name.className = 'org-selector__name';
        name.textContent = organisation.name;

        const code = document.createElement('span');
        code.className = 'org-selector__code';
        code.textContent = organisation.organisationCode;

        text.append(name, code);
        option.append(input, text);
        list.append(option);
    }
}

function selectedRadioValue(list: HTMLElement): string {
    return list.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.value ?? '';
}

function syncConfirmEnabled(list: HTMLElement, confirmBtn: HTMLButtonElement): void {
    confirmBtn.disabled = selectedRadioValue(list) === '';
}
